/* eslint-disable import/extensions */
import enMessages from '../../messages/en.json';
import deMessages from '../../messages/de.json';
import esMessages from '../../messages/es-ES.json';
import frMessages from '../../messages/fr.json';
import ptMessages from '../../messages/pt-PT.json';
import itMessages from '../../messages/it.json';
/* eslint-enable import/extensions */

// Every language the platform can support. `nativeName` is what the switcher
// shows (a language picker labels each option in its own language). The codes
// for Portuguese and Spanish carry a region on purpose — a bare `pt` makes
// Intl format numbers Brazilian-style, and the code is what tells the
// translation script which variant to produce. See docs/multi-language-spec.md §5.1.
export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  {
    code: 'es-ES',
    name: 'Spanish (Spain)',
    nativeName: 'Español',
    direction: 'ltr'
  },
  { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr' },
  {
    code: 'pt-PT',
    name: 'Portuguese (Portugal)',
    nativeName: 'Português',
    direction: 'ltr'
  },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', direction: 'ltr' }
];

// Static catalogue imports — enabled locales are ~10-15 KB each and cost less
// than one chart dependency, so import rather than lazy-load (§5.5). Add an
// entry here when a new locale's catalogue exists.
export const catalogues = {
  en: enMessages,
  de: deMessages,
  'es-ES': esMessages,
  fr: frMessages,
  'pt-PT': ptMessages,
  it: itMessages
};

// The GATSBY_ prefix is required for Gatsby to expose the var to the browser
// bundle. Falls back to English-only, which keeps every existing instance
// behaving exactly as it does today (isMultiLingual === false).
const enabledCodes = (process.env.GATSBY_ENABLED_LANGUAGES ?? 'en')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);

export const defaultLanguage = process.env.GATSBY_DEFAULT_LANGUAGE ?? 'en';

export const enabledLanguages = supportedLanguages.filter((l) =>
  enabledCodes.includes(l.code)
);

export const isMultiLingual = enabledLanguages.length > 1;

// Named date/time presets for use-intl's useFormatter (mirrors galsana's
// `formats` in src/i18n/config.ts). Extend as surfaces need them.
export const formats = {
  dateTime: {
    short: { day: 'numeric', month: 'short', year: 'numeric' }
  }
};

const byCode = new Map(
  supportedLanguages.map((l) => [l.code.toLowerCase(), l])
);

// Resolve an arbitrary locale tag (user pref, localStorage, navigator) against
// the ENABLED set, two-pass: exact match first, then base-language, so a
// browser reporting `pt-BR` resolves to the enabled `pt-PT`. Returns a code
// from enabledLanguages, or defaultLanguage. See §5.3.
export function resolveEnabledLanguage(tag) {
  if (!tag || typeof tag !== 'string') return defaultLanguage;
  const lower = tag.toLowerCase();

  const exact = enabledLanguages.find((l) => l.code.toLowerCase() === lower);
  if (exact) return exact.code;

  const base = lower.split('-')[0];
  const baseMatch = enabledLanguages.find(
    (l) => l.code.toLowerCase().split('-')[0] === base
  );
  if (baseMatch) return baseMatch.code;

  return defaultLanguage;
}

export function getDirection(code) {
  return byCode.get(code.toLowerCase())?.direction ?? 'ltr';
}
